import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowUpRight,
  Bell,
  Car,
  ClipboardCheck,
  Fuel,
  ShieldAlert,
  Sparkles,
  Users,
  Wrench,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/store';
import { formatBRL, formatDate } from '@/lib/format';
import { PRODUCT } from '@/config/product';
import { BaixarApp } from './baixar-app';

// ----- Tipos --------------------------------------------------------------

interface TenantCurrentResponse {
  data: {
    id: string;
    name: string;
    cnpj: string;
    status: string;
    trialEndsAt: string | null;
    plan: { code: string; name: string; maxVehicles: number | null };
  };
}

interface TenantUsageResponse {
  data: {
    users: number;
    vehicles: number;
    drivers: number;
    activeVehicles: number;
    checklistsToday: number;
    checklistsTotal: number;
  };
}

interface MaintenanceDashboardResponse {
  data: {
    plans: { overdue: number; upcoming: number };
    orders: {
      draft: number;
      approved: number;
      inProgress: number;
      completed: number;
      cancelled: number;
    };
    totalCostCents: number;
  };
}

interface FinesSummaryResponse {
  data: {
    totalFines: number;
    totalAmountCents: number;
    paidCount: number;
    pendingCount: number;
    overdueCount: number;
  };
}

interface FuelingSummaryResponse {
  data: {
    totalLiters: number;
    totalCostCents: number;
    avgConsumptionKmPerL: number | null;
    avgPriceCentsPerL: number;
    recordCount: number;
    anomalyCount: number;
    costPerKmCents: number | null;
  };
}

interface UnreadCountResponse {
  data: { count: number };
}

interface RecentVehicle {
  id: string;
  plate: string;
  brand: string | null;
  model: string | null;
}

interface RecentOrder {
  id: string;
  status: string;
  scheduledFor: string | null;
  completedAt: string | null;
  createdAt: string;
  totalCostCents: number;
  vehicle?: RecentVehicle | null;
}

interface RecentFueling {
  id: string;
  fueledAt: string;
  totalCents: number;
  quantityMl: number;
  isAnomaly: boolean;
  vehicle?: RecentVehicle | null;
}

interface RecentChecklist {
  id: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  vehicle?: RecentVehicle | null;
}

interface ListResponse<T> {
  data: T[];
  meta?: { page: number; limit: number; total: number };
}

// ----- Hooks --------------------------------------------------------------

function useTenantCurrent() {
  return useQuery({
    queryKey: ['tenant', 'current'],
    queryFn: async () => {
      const { data } = await api.get<TenantCurrentResponse>('/tenants/current');
      return data.data;
    },
  });
}

function useTenantUsage() {
  return useQuery({
    queryKey: ['tenant', 'usage'],
    queryFn: async () => {
      const { data } = await api.get<TenantUsageResponse>('/tenants/current/usage');
      return data.data;
    },
  });
}

function useMaintenanceDashboard() {
  return useQuery({
    queryKey: ['maintenance', 'dashboard'],
    queryFn: async () => {
      const { data } = await api.get<MaintenanceDashboardResponse>('/maintenance/dashboard');
      return data.data;
    },
  });
}

function useFinesSummary() {
  return useQuery({
    queryKey: ['fines', 'summary'],
    queryFn: async () => {
      const { data } = await api.get<FinesSummaryResponse>('/fines/summary');
      return data.data;
    },
  });
}

function useFuelingSummary() {
  return useQuery({
    queryKey: ['fueling', 'summary'],
    queryFn: async () => {
      const { data } = await api.get<FuelingSummaryResponse>('/fueling/summary');
      return data.data;
    },
  });
}

function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await api.get<UnreadCountResponse>('/notifications/unread-count');
      return data.data.count;
    },
    refetchInterval: 30_000,
  });
}

function useRecentOrders() {
  return useQuery({
    queryKey: ['maintenance', 'orders', 'recent'],
    queryFn: async () => {
      const { data } = await api.get<ListResponse<RecentOrder>>('/maintenance/orders', {
        params: { limit: 5, sort: '-createdAt' },
      });
      return data.data;
    },
  });
}

function useRecentFueling() {
  return useQuery({
    queryKey: ['fueling', 'recent'],
    queryFn: async () => {
      const { data } = await api.get<ListResponse<RecentFueling>>('/fueling', {
        params: { limit: 5, sort: '-fueledAt' },
      });
      return data.data;
    },
  });
}

function useRecentChecklists() {
  return useQuery({
    queryKey: ['checklists', 'executions', 'recent'],
    queryFn: async () => {
      const { data } = await api.get<ListResponse<RecentChecklist>>('/checklists/executions', {
        params: { limit: 5, sort: '-startedAt' },
      });
      return data.data;
    },
  });
}

// ----- KPI ----------------------------------------------------------------

interface KpiCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
  tone?: 'default' | 'success' | 'warning';
  loading?: boolean;
}

function KpiCard({ title, value, icon: Icon, hint, tone = 'default', loading }: KpiCardProps) {
  const toneClasses =
    tone === 'success'
      ? 'bg-success/10 text-success'
      : tone === 'warning'
        ? 'bg-warning/15 text-warning-foreground'
        : 'bg-primary/10 text-primary';

  return (
    <Card>
      <CardContent className="flex items-start justify-between p-5">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {title}
          </p>
          {loading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <p className="font-mono text-3xl font-semibold tracking-tight tabular-nums">
              {value}
            </p>
          )}
          {hint && !loading && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <div className={`flex size-10 items-center justify-center rounded-lg ${toneClasses}`}>
          <Icon className="size-5" />
        </div>
      </CardContent>
    </Card>
  );
}

// ----- Card "Precisa de atenção" -----------------------------------------

interface AttentionItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  hint?: string;
  to: string;
  tone: 'neutral' | 'warning' | 'danger';
  loading?: boolean;
}

function AttentionItem({ icon: Icon, label, count, hint, to, tone, loading }: AttentionItemProps) {
  const toneClasses =
    tone === 'danger'
      ? 'border-destructive/30 bg-destructive/5'
      : tone === 'warning'
        ? 'border-warning/40 bg-warning/5'
        : 'border-border';
  const iconClasses =
    tone === 'danger'
      ? 'bg-destructive/15 text-destructive'
      : tone === 'warning'
        ? 'bg-warning/20 text-warning-foreground'
        : 'bg-muted text-muted-foreground';
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded-lg border ${toneClasses} p-3 transition-colors hover:bg-accent/40`}
    >
      <div className={`flex size-9 shrink-0 items-center justify-center rounded-md ${iconClasses}`}>
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        {hint && <p className="truncate text-xs text-muted-foreground">{hint}</p>}
      </div>
      {loading ? (
        <Skeleton className="h-6 w-8" />
      ) : (
        <span className="font-mono text-lg font-semibold tabular-nums">{count}</span>
      )}
      <ArrowUpRight className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
}

// ----- Listas de atividade recente ---------------------------------------

const CHECKLIST_STATUS_LABEL: Record<string, string> = {
  in_progress: 'Em andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

const ORDER_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  approved: 'Aprovada',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

function RecentList({
  title,
  to,
  loading,
  empty,
  children,
}: {
  title: string;
  to: string;
  loading?: boolean;
  empty?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
          <Link to={to}>
            Ver todos
            <ArrowUpRight className="size-3" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {loading ? (
          [1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)
        ) : empty ? (
          <p className="py-6 text-center text-xs text-muted-foreground">Nada por aqui ainda.</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ----- Dashboard ---------------------------------------------------------

export function DashboardPage() {
  const [appInstructionsOpen, setAppInstructionsOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const tenantQuery = useTenantCurrent();
  const usageQuery = useTenantUsage();
  const maintenanceQuery = useMaintenanceDashboard();
  const finesQuery = useFinesSummary();
  const fuelingQuery = useFuelingSummary();
  const unreadCountQuery = useUnreadCount();
  const recentOrdersQuery = useRecentOrders();
  const recentFuelingQuery = useRecentFueling();
  const recentChecklistsQuery = useRecentChecklists();

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const firstName = user?.name?.split(' ')[0] ?? '';

  // Steps dinâmicos baseados em usage
  const usage = usageQuery.data;
  const steps = [
    {
      done: !!tenantQuery.data,
      title: 'Criar empresa',
      desc: 'Cadastro concluído. Cadastre a frota para saber quanto vai pagar.',
    },
    {
      done: (usage?.vehicles ?? 0) > 0,
      title: 'Cadastrar veículos da frota',
      desc: 'Placas, modelos, tipo de combustível.',
      to: '/app/vehicles',
    },
    {
      done: (usage?.drivers ?? 0) > 0 || (usage?.users ?? 0) > 1,
      title: 'Convidar motoristas e gestores',
      desc: 'Envio de convite por e-mail com definição de papel.',
      to: '/app/users',
    },
    {
      done: (usage?.checklistsTotal ?? 0) > 0,
      title: 'Realizar o primeiro checklist',
      desc: `Veja como instalar e liberar o app ${PRODUCT.name} para eles.`,
      action: 'openAppInstructions' as const,
    },
  ];
  const allStepsDone = !usageQuery.isLoading && steps.every((s) => s.done);

  // Precisa de atenção
  const maintenance = maintenanceQuery.data;
  const fines = finesQuery.data;
  const fueling = fuelingQuery.data;
  const overdueMaintenance = (maintenance?.plans.overdue ?? 0) + (maintenance?.orders.inProgress ?? 0);
  const openFines = (fines?.pendingCount ?? 0);
  const anomalies = fueling?.anomalyCount ?? 0;
  const unreadNotifs = unreadCountQuery.data ?? 0;
  const attentionTotal = overdueMaintenance + openFines + anomalies + unreadNotifs;

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">Visão geral da sua operação</p>
          <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
            {greeting}, {firstName} 👋
          </h1>
          {tenantQuery.data && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge variant="info">{tenantQuery.data.name}</Badge>
            </div>
          )}
        </div>

        <Button variant="outline" size="sm" className="self-start">
          <Sparkles className="size-4" />
          Tour do produto
        </Button>
      </header>

      {/* KPIs principais */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Veículos ativos"
          value={String(usageQuery.data?.activeVehicles ?? 0)}
          icon={Car}
          hint={`${usageQuery.data?.vehicles ?? 0} cadastrados no total`}
          loading={usageQuery.isLoading}
        />
        <KpiCard
          title="Motoristas"
          value={String(usageQuery.data?.drivers ?? 0)}
          icon={Users}
          hint="cadastrados na operação"
          loading={usageQuery.isLoading}
        />
        <KpiCard
          title="Usuários do painel"
          value={String(usageQuery.data?.users ?? 0)}
          icon={Users}
          hint="com acesso ao sistema"
          loading={usageQuery.isLoading}
          tone="success"
        />
        <KpiCard
          title="Checklists hoje"
          value={String(usageQuery.data?.checklistsToday ?? 0)}
          icon={ClipboardCheck}
          hint={`${usageQuery.data?.checklistsTotal ?? 0} concluídos no total`}
          loading={usageQuery.isLoading}
          tone="warning"
        />
      </section>

      {/* Primeiros passos — só aparece se houver passo pendente */}
      {!allStepsDone && (
        <section>
          <Card>
            <CardHeader>
              <CardTitle>Primeiros passos</CardTitle>
              <CardDescription>
                Configure sua frota em poucos minutos para começar a operar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {steps.map((step, idx) => {
                const content = (
                  <>
                    <div
                      className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        step.done
                          ? 'bg-success text-success-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {step.done ? '✓' : idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{step.title}</p>
                      <p className="text-xs text-muted-foreground">{step.desc}</p>
                    </div>
                    {step.done ? (
                      <Badge variant="success">Feito</Badge>
                    ) : (
                      <ArrowUpRight className="size-4 text-muted-foreground" />
                    )}
                  </>
                );
                const className =
                  'flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:bg-accent/40';
                if (step.done) {
                  return (
                    <div key={idx} className={className}>
                      {content}
                    </div>
                  );
                }
                if ('action' in step && step.action === 'openAppInstructions') {
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setAppInstructionsOpen(true)}
                      className={className}
                    >
                      {content}
                    </button>
                  );
                }
                if ('to' in step && step.to) {
                  return (
                    <Link key={idx} to={step.to} className={className}>
                      {content}
                    </Link>
                  );
                }
                return (
                  <div key={idx} className={className}>
                    {content}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </section>
      )}

      {/* App do motorista.
          Fica FORA do bloco de "Primeiros passos" de propósito: aquele some
          quando o onboarding termina, e o download precisa continuar à mão —
          é o que se procura ao contratar cada motorista novo, não uma vez só.
          Antes ele vivia dentro de um diálogo, atrás de dois cliques, e
          ninguém encontrava. */}
      <section>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">App do motorista</CardTitle>
            <CardDescription>
              O motorista usa o celular para checklists, abastecimento e
              ocorrências. Instale uma vez por aparelho.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BaixarApp />
          </CardContent>
        </Card>
      </section>


      {/* Precisa de atenção — só aparece se houver itens */}
      {attentionTotal > 0 && (
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="size-4 text-warning-foreground" />
                Precisa de atenção
              </CardTitle>
              <CardDescription>Itens em aberto na sua operação.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <AttentionItem
                icon={Wrench}
                label="Manutenção em aberto"
                count={overdueMaintenance}
                hint={
                  maintenance
                    ? `${maintenance.plans.overdue} planos atrasados · ${maintenance.orders.inProgress} OS em andamento`
                    : undefined
                }
                to="/app/maintenance"
                tone={
                  (maintenance?.plans.overdue ?? 0) > 0
                    ? 'danger'
                    : overdueMaintenance > 0
                      ? 'warning'
                      : 'neutral'
                }
                loading={maintenanceQuery.isLoading}
              />
              <AttentionItem
                icon={ShieldAlert}
                label="Multas em aberto"
                count={openFines}
                hint={
                  fines
                    ? `${fines.overdueCount} vencidas · ${formatBRL(fines.totalAmountCents)} no total`
                    : undefined
                }
                to="/app/fines"
                tone={
                  (fines?.overdueCount ?? 0) > 0 ? 'danger' : openFines > 0 ? 'warning' : 'neutral'
                }
                loading={finesQuery.isLoading}
              />
              <AttentionItem
                icon={Fuel}
                label="Anomalias de abastecimento"
                count={anomalies}
                hint={fueling ? `${fueling.recordCount} registros analisados` : undefined}
                to="/app/fueling"
                tone={anomalies > 0 ? 'warning' : 'neutral'}
                loading={fuelingQuery.isLoading}
              />
              <AttentionItem
                icon={Bell}
                label="Notificações não lidas"
                count={unreadNotifs}
                to="/app/notifications"
                tone={unreadNotifs > 0 ? 'warning' : 'neutral'}
                loading={unreadCountQuery.isLoading}
              />
            </CardContent>
          </Card>
        </section>
      )}

      {/* Atividade recente */}
      <section className="grid gap-4 lg:grid-cols-3">
        <RecentList
          title="Últimas manutenções"
          to="/app/maintenance"
          loading={recentOrdersQuery.isLoading}
          empty={!recentOrdersQuery.data?.length}
        >
          {recentOrdersQuery.data?.map((o) => (
            <div
              key={o.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  <span className="font-mono">{o.vehicle?.plate ?? '—'}</span>
                  <span className="ml-2 font-normal text-muted-foreground">
                    {formatDate(o.scheduledFor ?? o.createdAt)}
                  </span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {ORDER_STATUS_LABEL[o.status] ?? o.status}
                </p>
              </div>
              <span className="font-mono text-xs tabular-nums">
                {formatBRL(o.totalCostCents)}
              </span>
            </div>
          ))}
        </RecentList>

        <RecentList
          title="Últimos abastecimentos"
          to="/app/fueling"
          loading={recentFuelingQuery.isLoading}
          empty={!recentFuelingQuery.data?.length}
        >
          {recentFuelingQuery.data?.map((f) => (
            <div
              key={f.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  <span className="font-mono">{f.vehicle?.plate ?? '—'}</span>
                  <span className="ml-2 font-normal text-muted-foreground">
                    {formatDate(f.fueledAt)}
                  </span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {(f.quantityMl / 1000).toFixed(1)} L
                  {f.isAnomaly && (
                    <span className="ml-1 text-warning-foreground">· anomalia</span>
                  )}
                </p>
              </div>
              <span className="font-mono text-xs tabular-nums">{formatBRL(f.totalCents)}</span>
            </div>
          ))}
        </RecentList>

        <RecentList
          title="Últimos checklists"
          to="/app/checklists"
          loading={recentChecklistsQuery.isLoading}
          empty={!recentChecklistsQuery.data?.length}
        >
          {recentChecklistsQuery.data?.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-2 rounded-md border border-border p-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  <span className="font-mono">{c.vehicle?.plate ?? '—'}</span>
                  <span className="ml-2 font-normal text-muted-foreground">
                    {formatDate(c.startedAt)}
                  </span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {CHECKLIST_STATUS_LABEL[c.status] ?? c.status}
                </p>
              </div>
            </div>
          ))}
        </RecentList>
      </section>

      <AppInstructionsDialog
        open={appInstructionsOpen}
        onOpenChange={setAppInstructionsOpen}
        managerEmail={user?.email}
      />
    </div>
  );
}

function AppInstructionsDialog({
  open,
  onOpenChange,
  managerEmail,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  managerEmail?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Instalar o app no celular do motorista</DialogTitle>
          <DialogDescription>
            Quatro passos para o motorista começar a rodar checklists no
            celular.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                1
              </span>
              <div>
                <p className="font-medium">Cadastre o motorista no painel</p>
                <p className="text-xs text-muted-foreground">
                  Em <strong>Motoristas → Novo motorista</strong>, marque{' '}
                  <strong>"Criar acesso ao aplicativo"</strong> e defina uma senha
                  inicial (mínimo 10 caracteres).
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                2
              </span>
              <div>
                <p className="font-medium">Vincule um veículo</p>
                <p className="text-xs text-muted-foreground">
                  Na tela do motorista, aba <strong>"Veículos"</strong>, associe
                  ao veículo que ele vai operar. Sem isso o app fica sem o
                  veículo configurado.
                </p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                3
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium">Instale o app no celular dele</p>
                <p className="text-xs text-muted-foreground">
                  Mostre o QR Code abaixo para o motorista, ou envie o link
                  para o celular dele.
                </p>
                <div className="mt-2">
                  <BaixarApp />
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                4
              </span>
              <div>
                <p className="font-medium">Passe as credenciais</p>
                <p className="text-xs text-muted-foreground">
                  Compartilhe com o motorista por canal seguro: <strong>e-mail
                  cadastrado + senha inicial</strong>. Ele pode trocar a senha
                  depois na tela de Perfil dele.
                </p>
              </div>
            </li>
          </ol>

          {managerEmail && (
            <p className="text-[11px] text-muted-foreground">
              Você está logado como <strong>{managerEmail}</strong>.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Entendi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
